/**
 * Plain-English glossary of the abbreviations we use across the site.
 *
 * Each entry has the full wording (`full`) and a simple, jargon-free
 * explanation (`text`) aimed at clients who aren't technical. The
 * GlossaryEnhancer component automatically finds these acronyms anywhere
 * in the page content and turns them into hover/tap-able terms that reveal
 * the matching explanation.
 *
 * UK spelling is used deliberately (Optimisation, not Optimization) to
 * match the rest of the site copy.
 */
export interface GlossaryEntry {
  /** The expanded wording, e.g. "Search Engine Optimisation". */
  full: string;
  /** A short, plain-English explanation for non-technical clients. */
  text: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  SEO: {
    full: "Search Engine Optimisation",
    text: "Making your website show up higher in Google's normal (unpaid) search results, so the right customers can find you.",
  },
  AEO: {
    full: "Answer Engine Optimisation",
    text: "Setting your business up to be the answer that AI assistants and voice search read out — not just one link in a long list.",
  },
  GEO: {
    full: "Generative Engine Optimisation",
    text: "Getting your business mentioned and cited by AI tools like ChatGPT, Gemini, Perplexity and Google's AI answers.",
  },
  SERP: {
    full: "Search Engine Results Page",
    text: "The page of results you see after typing something into Google. Ranking higher on it means more people find you.",
  },
  PPC: {
    full: "Pay-Per-Click",
    text: "Paid adverts (like Google Ads) where you only pay when someone actually clicks your ad — handy for fast, targeted traffic.",
  },
  GTM: {
    full: "Google Tag Manager",
    text: "A free Google tool that lets us manage the tracking codes on your website without digging into the code each time.",
  },
  CRO: {
    full: "Conversion Rate Optimisation",
    text: "Improving your website so more of your visitors actually take action — call you, buy, or send an enquiry.",
  },
  ROI: {
    full: "Return On Investment",
    text: "How much money you get back compared with what you spent. A simple way to see if something is paying off.",
  },
  UX: {
    full: "User Experience",
    text: "How easy, clear and pleasant your website or app feels to use for the people visiting it.",
  },
  UI: {
    full: "User Interface",
    text: "The part of a website or app people actually see and tap — the buttons, menus, screens and layout.",
  },
  API: {
    full: "Application Programming Interface",
    text: "A way for two pieces of software to talk to each other automatically, so your tools can share information.",
  },
  GDPR: {
    full: "General Data Protection Regulation",
    text: "UK and EU law covering how you collect, store and use people's personal data. We build your site to respect it.",
  },
  CMS: {
    full: "Content Management System",
    text: "Software (like WordPress) that lets you update your own website's words and images without needing a developer.",
  },
  SaaS: {
    full: "Software as a Service",
    text: "Software you use online by subscription — there's nothing to install, and updates happen automatically.",
  },
  GMB: {
    full: "Google Business Profile",
    text: "Your free business listing on Google Maps and Search (formerly 'Google My Business') — vital for being found locally.",
  },
  CTA: {
    full: "Call To Action",
    text: "The button or prompt that asks a visitor to do something, like 'Get a quote' or 'Book a call'.",
  },
  KPI: {
    full: "Key Performance Indicator",
    text: "A key number you track to check you're hitting your goals — for example enquiries per month.",
  },
  CTR: {
    full: "Click-Through Rate",
    text: "The percentage of people who click after seeing your link or ad. Higher usually means your message is landing.",
  },
  B2B: {
    full: "Business to Business",
    text: "Selling your products or services to other companies rather than directly to the public.",
  },
  B2C: {
    full: "Business to Consumer",
    text: "Selling directly to members of the public rather than to other businesses.",
  },
  LLM: {
    full: "Large Language Model",
    text: "The kind of AI behind tools like ChatGPT that can understand and write human-sounding text.",
  },
};

/** Escapes a string for safe use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Builds a single case-sensitive regex that matches any glossary term as a
 * whole word. Longer terms are listed first so multi-letter matches win.
 * Lookarounds (rather than \b) keep mixed-case terms like "SaaS" reliable.
 */
export function buildGlossaryRegex(): RegExp {
  const terms = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length).map(escapeRegExp);
  return new RegExp(`(?<![\\w])(${terms.join("|")})(?![\\w])`, "g");
}

/** Looks up an entry by its matched text. */
export function lookupTerm(term: string): GlossaryEntry | undefined {
  return GLOSSARY[term];
}
